/**
 * ConfidenceBreakdownBar
 *
 * Horizontal stacked bar showing the proportional contribution of
 * six confidence subscores.  Each segment is labeled beneath the bar.
 *
 * Subscore keys:
 *   ec  - Evidence Continuity
 *   efc - Evidence Freshness & Coverage
 *   ei  - Evidence Integrity
 *   os  - Operational Stability
 *   rd  - Risk & Deviation
 *   bre - Business Rule Enforcement
 */
import React from 'react';
import { makeStyles, tokens, Caption1 } from '@fluentui/react-components';
import { solutionTokens, semanticColors, categoryColors } from '../../theme/solution-theme';

/* ------------------------------------------------------------------ */
/*  Segment metadata                                                   */
/* ------------------------------------------------------------------ */

interface SegmentMeta {
  key: keyof ConfidenceSubscores;
  label: string;
  color: string;
}

// #0078D4 (classic Fluent "communication blue") is not in brand-config.json's
// palette — no token represents it, left as a raw literal.
const SEGMENTS: SegmentMeta[] = [
  { key: 'ec', label: 'Evidence', color: '#0078D4' },
  { key: 'efc', label: 'Freshness', color: tokens.colorBrandBackground },
  { key: 'ei', label: 'Integrity', color: semanticColors.success },
  { key: 'os', label: 'Stability', color: solutionTokens.brandSecondary },
  { key: 'rd', label: 'Risk', color: semanticColors.warning },
  { key: 'bre', label: 'Rules', color: categoryColors.code },
];

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    width: '100%',
  },
  barTrack: {
    display: 'flex',
    width: '100%',
    height: '10px',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground4,
  },
  segment: {
    height: '100%',
    transition: 'width 0.3s ease',
    minWidth: '2px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
});

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ConfidenceSubscores {
  /** Evidence Continuity */
  ec: number;
  /** Evidence Freshness & Coverage */
  efc: number;
  /** Evidence Integrity */
  ei: number;
  /** Operational Stability */
  os: number;
  /** Risk & Deviation */
  rd: number;
  /** Business Rule Enforcement */
  bre: number;
}

export interface ConfidenceBreakdownBarProps {
  /** Six subscore values (0-100 each). Proportional widths are computed automatically. */
  subscores: ConfidenceSubscores;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const ConfidenceBreakdownBar: React.FC<ConfidenceBreakdownBarProps> = ({
  subscores,
}) => {
  const styles = useStyles();

  const total = SEGMENTS.reduce((sum, s) => sum + Math.max(0, subscores[s.key]), 0);

  return (
    <div className={styles.root} aria-label="Confidence subscore breakdown">
      {/* Stacked bar */}
      <div className={styles.barTrack}>
        {SEGMENTS.map((seg) => {
          const value = Math.max(0, subscores[seg.key]);
          const pct = total > 0 ? (value / total) * 100 : 0;
          return (
            <div
              key={seg.key}
              className={styles.segment}
              style={{ width: `${pct}%`, backgroundColor: seg.color }}
              title={`${seg.label}: ${value}`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className={styles.legend}>
        {SEGMENTS.map((seg) => (
          <span key={seg.key} className={styles.legendItem}>
            <span
              className={styles.legendDot}
              style={{ backgroundColor: seg.color }}
            />
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {seg.label} {subscores[seg.key]}
            </Caption1>
          </span>
        ))}
      </div>
    </div>
  );
};
